// --------------------------------------------------------------
// Created On: 2024-05-13
// Author: KC Willard
//
// Last Modified: 2025-11-06
// Modified By: Zachary Thomas
//
// Copyright 2024 - 2025 © Cornell Pump Company, All Rights Reserved
// --------------------------------------------------------------

import React, { useMemo, useState } from "react";
import styles from "./SaveCustomerAddressModal.module.scss";
import Modal from "../../../components/Modal/Modal";
import ModalHeader from "../../../components/ModalHeader/ModalHeader";
import ModalBody from "../../../components/ModalBody/ModalBody";
import ModalFooter from "../../../components/ModalFooter/ModalFooter";

// Component to allow selecting from a list of stored users or enter a name for a new one.
export default function SaveCustomerAddressModal(props: Props): Component {
  const [selectedCustomerAddressName, setSelectedCustomerAddressName] = useState<string>("");
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [inputCustomerAddressName, setInputCustomerAddressName] = useState<string>("");
  const showNameInput = selectedCustomerAddressName === "CREATE NEW SAVED CUSTOMER";
  const nameExists = useMemo(() => validateNameExists(), [inputCustomerAddressName]);

  // Function to check if the name already exists in the list of stored users.
  function validateNameExists() {
    if (props.savedCustomers.some((savedCustomer) => savedCustomer.name === inputCustomerAddressName)) {
      setErrorMessage(
        `The name "${inputCustomerAddressName}" already exists. Overwrite the previously saved information?`
      );
      return true;
    } else {
      setErrorMessage("");
      return false;
    }
  }
  // Function to pass the correct value to the "saveCustomer" function.
  function handleOnYes() {
    if (selectedCustomerAddressName === "CREATE NEW SAVED CUSTOMER" || selectedCustomerAddressName === "") {
      props.onYes(inputCustomerAddressName);
    } else {
      props.onYes(selectedCustomerAddressName);
    }
    setInputCustomerAddressName("");
    setSelectedCustomerAddressName("");
    props.onClose();
  }

  // Clear all inputs when modal is closed out by clicking away from it.
  function handleCloseModal(callBack: () => void) {
    setInputCustomerAddressName("");
    setSelectedCustomerAddressName("");
    callBack();
  }

  return (
    <div data-test="confirmation-modal">
      <Modal
        show={props.showModal}
        onHide={() => handleCloseModal(props.onClose)}
        backdropClassName={`${styles.modal} ${styles.backdrop}`}
        style={{ zIndex: "var(--super-modal-z-index)" }}
        animation
        centered
      >
        <ModalHeader>
          <h5>{props.title}</h5>
        </ModalHeader>
        <ModalBody>
          <p className={`"${styles.label} mx-2 mb-2"`}>{props.content}</p>
          <div>
            <div className="mx-2 mb-2">
              <div className="row align-items-center">
                {/* Display a dropdown if any saved addresses exist, otherwise just display the input field. */}
                {props.savedCustomers.length > 0 ? (
                  <div>
                    <select
                      data-test="saved-customer-selection"
                      className="form-select"
                      value={selectedCustomerAddressName}
                      onChange={(e) => setSelectedCustomerAddressName(e.target.value)}
                    >
                      <option value="" disabled>
                        Select a saved customer to save shipping information
                      </option>
                      <option value="CREATE NEW SAVED CUSTOMER">Save as new customer</option>
                      {props.savedCustomers.map((savedCustomer) => (
                        <option value={savedCustomer.name} key={savedCustomer.savedCustomerAddressId}>
                          {savedCustomer.name}
                        </option>
                      ))}
                    </select>
                    {showNameInput && (
                      <div>
                        <div className={`${styles.customerNameInput}`}>
                          <input
                            className="form-control"
                            data-test="customer-name-input"
                            type="text"
                            maxLength={100}
                            aria-describedby="customer"
                            placeholder="Enter a name for this saved customer"
                            value={inputCustomerAddressName}
                            onChange={(e) => setInputCustomerAddressName(e.target.value)}
                          />
                        </div>
                        {nameExists && (
                          <div className={`${styles.errorMessage}`}>
                            <span>{errorMessage}</span>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ) : (
                  <div className={`${styles.customerNameInput}`}>
                    <input
                      className="form-control"
                      data-test="customer-name-input"
                      type="text"
                      maxLength={100}
                      aria-describedby="customer"
                      placeholder="Enter a name for this saved customer"
                      value={inputCustomerAddressName}
                      onChange={(e) => setInputCustomerAddressName(e.target.value)}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
        </ModalBody>
        <ModalFooter>
          {props.yesText !== undefined && (
            <button
              data-test="save-customer-modal-confirm-button"
              type="button"
              className={`btn ${props.danger ? "btn-danger" : "btn-primary"}`}
              data-dismiss="modal"
              onClick={() => handleOnYes()}
            >
              {props.yesText}
            </button>
          )}
          {props.noText !== undefined && (
            <button
              data-test="save-customer-modal-cancel-button"
              type="button"
              className="btn btn-secondary"
              onClick={() => handleCloseModal(props.onNo)}
            >
              {props.noText}
            </button>
          )}
        </ModalFooter>
      </Modal>
    </div>
  );
}

interface Props {
  showModal: boolean;
  title: string;
  content: string | string[];
  yesText?: string;
  noText?: string;
  danger?: boolean;
  onClose: () => void;
  onYes: (customerAddressName: string) => void;
  onNo: () => void;
  savedCustomers: SavedCustomer[];
}

interface SavedCustomer {
  savedCustomerAddressId: number;
  name: string;
}
